import { v4 as uuidv4 } from 'uuid';
import { useState, useRef } from 'react';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { CopyPlus } from 'lucide-react';

import { getApiUrl } from '@/helpers/api';
import Spinner from '@/components/common/Spinner';

import { usePostCoachProfileImage } from '@/data/coach';

const getBucketUrl = () => {
  // eslint-disable-next-line no-undef
  if (process.env.VITE_NODE_ENV === 'development') {
    return 'https://profound-img-dev.s3.us-west-2.amazonaws.com/public';
  }
  return 'https://profound-img.s3.us-west-2.amazonaws.com/public';
};

const ImageUploader = ({ setError, coach, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);

  const postCoachProfileImage = usePostCoachProfileImage();
  const fileInputRef = useRef(null);

  const onUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    setSubmitting(true);
    setError('');
    const file = event.target.files[0];
    const options = [
      {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 200,
        useWebWorker: true,
        fileType: 'image/webp'
      }, // Thumbnail
      {
        maxSizeMB: 0.6,
        maxWidthOrHeight: 720,
        useWebWorker: true,
        fileType: 'image/webp'
      }, // Medium
      {
        maxSizeMB: 1,
        maxWidthOrHeight: 1080,
        useWebWorker: true,
        fileType: 'image/webp'
      } // Full-size
    ];

    try {
      const [thumb, medium, full] = await Promise.all(
        options.map((option) => imageCompression(file, option))
      );
      const compressedFiles = { full, medium, thumb };

      const imageId = uuidv4();
      await Promise.all([
        uploadFile(compressedFiles.full, 'full', imageId),
        uploadFile(compressedFiles.medium, 'medium', imageId),
        uploadFile(compressedFiles.thumb, 'thumb', imageId)
      ]);

      await postCoachProfileImage({
        imageUrl: `${getBucketUrl()}/${imageId}-full.webp`,
        imageUrlMedium: `${getBucketUrl()}/${imageId}-medium.webp`,
        imageUrlThumb: `${getBucketUrl()}/${imageId}-thumb.webp`,
        sortOrder: coach.profileImages.length
      });
      onSuccess();
      fileInputRef.current.value = '';
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
      setError(JSON.stringify(error));
    }
  };

  const uploadFile = async (file, size, imageId) => {
    const response = await axios.post(
      `${getApiUrl()}/file/generate-presigned-url?type=image`,
      {
        fileName: `public/${imageId}-${size}.webp`,
        fileType: 'image/webp'
      }
    );
    const presignedUrl = response.data.url;

    // upload to s3
    try {
      await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': 'image/webp'
        }
      });
    } catch (error) {
      setError(JSON.stringify(error));
      setSubmitting(false);
    }
  };

  return (
    <>
      <input
        type="file"
        onChange={handleFileChange}
        ref={fileInputRef}
        className="hidden"
      />
      {submitting && <Spinner />}
      {!submitting && <CopyPlus onClick={onUploadClick} />}
    </>
  );
};

export default ImageUploader;
